import { useCallback, useEffect, useRef, useState } from "react";
import type { Achievement } from "../achievements";

const PARTICLE_COUNT = 10;
const PARTICLE_COLORS = [
  "var(--color-accent)",
  "#FFD700",
  "#FF6B6B",
  "#4ECDC4",
  "#A78BFA",
];

interface AchievementToastProps {
  readonly achievement: Achievement;
  readonly onDismiss: () => void;
  readonly duration?: number;
}

export function AchievementToast({
  achievement,
  onDismiss,
  duration = 5000,
}: AchievementToastProps) {
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const dismiss = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setExiting(true);
    setTimeout(() => onDismissRef.current(), 400);
  }, []);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        dismiss();
      }
    }, 50);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss is stable, duration doesn't change
  }, []);

  return (
    <div
      className={`pointer-events-auto ${
        exiting ? "animate-achievement-exit" : "animate-achievement-enter"
      }`}
    >
      <div className="relative">
        <ParticleBurst />

        <button
          type="button"
          onClick={dismiss}
          className="relative bg-surface border border-accent/30 px-6 py-5 rounded-2xl shadow-2xl shadow-accent/25 min-w-80 max-w-[90vw] cursor-pointer hover:border-accent/50 transition-colors"
        >
          <div className="absolute inset-0 rounded-2xl bg-accent/5 animate-achievement-glow pointer-events-none" />

          <div className="relative flex flex-col items-center gap-2.5 text-center">
            <div className="relative mb-1">
              <div className="absolute inset-[-8px] rounded-full border-2 border-accent/50 animate-achievement-ring" />
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent/15 animate-achievement-icon">
                {achievement.Icon && <achievement.Icon className="w-7 h-7 text-accent" />}
              </div>
            </div>

            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent animate-achievement-text-1">
              Accomplissement débloqué
            </p>
            <p className="text-sm font-bold text-ink animate-achievement-text-2">
              {achievement.title}
            </p>
            <p className="text-xs text-ink-muted animate-achievement-text-3">
              {achievement.description}
            </p>
          </div>

          <div className="relative mt-4 h-0.5 bg-accent/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent to-accent/50 rounded-full transition-all ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </button>
      </div>
    </div>
  );
}

function ParticleBurst() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: PARTICLE_COUNT }, (_, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-8 w-1.5 h-1.5 rounded-full animate-achievement-particle"
          style={{
            backgroundColor: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
            "--particle-angle": `${(360 / PARTICLE_COUNT) * i}deg`,
            animationDelay: `${0.15 + i * 0.04}s`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}
